import * as React from 'react';
import { parseISO, format, addDays, addMonths, startOfWeek, endOfWeek, startOfMonth, endOfMonth,
  isSameMonth, isSameDay, isValid, isBefore, differenceInDays, isWeekend } from 'date-fns';
import './styles/calendar';

import { dateFnsLocale } from '../utils/dateHelper';
import { isActiveDate } from './helpers';
import t from './locales';
import { IPrice, IPriceTable, ICurrency } from '../ShoppingCart/types';
import { priceWithCommas } from '../utils/commonHelper';
import { addNumberDelimiterForPrice } from '../utils/stringUtil';
import { getLocale } from '../utils/pathHelper';

/* Types  */

interface IProps {
  minDate?: Date;
  maxDate?: Date;
  isRangeSelect: boolean;
  maxRange?: number;
  dateDuration: number;
  selectedDateList?: Date[];
  payableDateList?: Date[];
  priceTable: IPriceTable;
  handleDateListChange?: Function;
  localCurrency?: ICurrency;
}

interface IState {
  currentMonth: Date;
  selectedDateList: Date[];
  payableDateList: Date[];
}

/* Utils  */

function priceOnTheDay(priceTable: IPriceTable, day: Date): IPrice {
  const dateString: string = format(day, 'yyyy-MM-dd');
  return priceTable[dateString];
}

function isPayable(priceTable: IPriceTable, day: Date): boolean {
  const price = priceOnTheDay(priceTable, day);
  if (price == null) { return false; }
  const { adultPrice, childPrice, groupPrice, studentPrice, seniorPrice } = price;
  return [adultPrice, childPrice, groupPrice, studentPrice, seniorPrice].some((p) => typeof(p) === 'number');
}

function calculatePayable(selectedDateList: Date[], dateDuration: number): Date[] {
  if (dateDuration === 1) { return selectedDateList; }
  const payable: Date[] = [];
  for (let i = 0; i < selectedDateList.length; i++) {
    if (i === 0 || (i + 1) % dateDuration === 1) {
      payable.push(selectedDateList[i]);
    }
  }
  return payable;
}

function getFirstDayCanBuy(priceTable: IPriceTable): Date {
  const firstDay = Object.entries(priceTable)
    .map(a => a[0])
    .sort((a, b) => a >= b ? 1 : -1)[0];
  return !!firstDay ? new Date(firstDay) : new Date();
}

/* React Component  */

class SnowItemCalendar extends React.Component<IProps, IState> {

  static defaultProps = {
    isRangeSelect: true,
    dateDuration: 1,
    selectedDateList: [],
    priceTable: {},
  }

  constructor(props: IProps) {
    super(props);
    this.state = {
      currentMonth: getFirstDayCanBuy(this.props.priceTable),
      selectedDateList: props.selectedDateList || [],
      payableDateList: props.payableDateList || [],
    }
  }

  updateDateList(selectedDateList: Date[], payableDateList: Date[]): void {
    this.setState({ selectedDateList, payableDateList }, () => {
      const { handleDateListChange } = this.props;
      if (typeof (handleDateListChange) === 'function') {
        handleDateListChange(selectedDateList, payableDateList);
      }
    });
  }

  isActiveDate(selected: Date): boolean {
    const { minDate, maxDate, maxRange, isRangeSelect } = this.props;
    const { selectedDateList } = this.state;
    return isActiveDate({
      targetDate: selected,
      minDate,
      maxDate,
      maxRange: isRangeSelect && maxRange ? maxRange : null,
      selectedDateList,
      checkTime: true
    });
  }

  handleDateClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    const { currentTarget } = event;
    const { isRangeSelect, dateDuration, priceTable } = this.props;
    const { selectedDateList } = this.state;
    const selected: Date = parseISO(currentTarget.getAttribute('data-date'));
    if (!isValid(selected)) { return; }
    if (!this.isActiveDate(selected)) { return; }
    if (!isPayable(priceTable, selected)) { return; }
    const isAlreadySelected = isSameDay(selected, selectedDateList[0]);
    const isSelectedBefore = selectedDateList[0] && isBefore(selected, selectedDateList[0]);
    const selectedCount = selectedDateList.length;
    if (isRangeSelect) {
      if (isSelectedBefore || selectedCount !== 1 || isAlreadySelected) {
        if (!isPayable(priceTable, selected)) { return; }
        this.updateDateList([selected], calculatePayable([selected], dateDuration))
      } else {
        let dateList: Date[] = [];
        let payable: Date[] = [];
        const selectedDuration: number = differenceInDays(selected, selectedDateList[0]) + 1;
        // 複数日券の場合は利用期間で割り切れる必要がある
        const multiplier = Math.ceil(selectedDuration / dateDuration);
        for (let i = 0; i < dateDuration * multiplier; i++) {
          dateList.push(addDays(selectedDateList[0], i));
        }
        payable = calculatePayable(dateList, dateDuration);
        // 1日券：ダメな日をまたいで購入できる
        // 連日券：価格表がすべての支払該当日をカバーしていなければ選択させない
        if (dateDuration === 1) {
          const dateAvailable = dateList.filter((d) => isPayable(priceTable, d));
          dateList = dateAvailable;
          payable = dateAvailable;
        } else {
          if (!payable.every((p) => isPayable(priceTable, p))) { alert(t.thisDateIsNotAvailable()); return; }
        }
        this.updateDateList(dateList, payable);
      }
    } else {
      if (!isPayable(priceTable, selected)) { return; }
      if (isAlreadySelected) {
        this.updateDateList([], []);
      } else {
        const dateList: Date[] = [];
        for (let i = 0; i < dateDuration; i++) {
          dateList.push(addDays(selected, i));
        }
        this.updateDateList(dateList, calculatePayable(dateList, dateDuration));
      }
    }
  }

  handleNextMonthClick = () => {
    this.setState({
      currentMonth: addMonths(this.state.currentMonth, 1)
    });
  }

  handlePrevMonthClick = () => {
    this.setState({
      currentMonth: addMonths(this.state.currentMonth, -1)
    });
  }

  renderHeader() {
    const { dateDuration } = this.props;
    const currentLocale = getLocale();
    let monthYearFormat = 'MMMM yyyy';

    if (currentLocale === 'ja') {
      monthYearFormat = 'yyyy年M月';
    }

    return (
      <div className="price-calendar__header">
        <div className="icon" onClick={this.handlePrevMonthClick}>
          <i className="fas fa-chevron-left" />
        </div>
        <div>
          <div className="month">{format(this.state.currentMonth, monthYearFormat, { locale: dateFnsLocale() })}</div>
          {dateDuration > 1 && 
            <div className="note">
              {t.thisIsConsecutiveDayPass({ dateDuration })} {t.pleaseSelectStartDate()}
            </div>}
        </div>
        <div className="icon" onClick={this.handleNextMonthClick}>
          <i className="fas fa-chevron-right" />
        </div>
      </div>
    );
  }

  renderDays() {
    const dateFormat = 'eee';
    const days = [];
    const startDate = startOfWeek(this.state.currentMonth);
    for (let i = 0; i < 7; i++) {
      days.push(
        <div key={`day-${i}`}>
          {format(addDays(startDate, i), dateFormat, { locale: dateFnsLocale() })}
        </div>
      );
    }
    return (
      <div className="price-calendar__days">
        {days}
      </div>
    );
  }

  renderPrice(day: Date) {
    const { priceTable } = this.props;
    const { localCurrency } = this.props
    if (!this.isActiveDate(day)) { return null; }
    if (!isPayable(priceTable, day)) { return null; }
    const price: IPrice = priceOnTheDay(priceTable, day);
    const elements: JSX.Element[] = [];
    const locale = getLocale();
    ['adultPrice', 'childPrice', 'groupPrice', 'studentPrice', 'seniorPrice'].forEach((key: string) => {
      if (price[key] && !!localCurrency) {
        elements.push(
          <div className="price-item" key={`${format(day, 'yyyy-MM-dd')}-${key}-price`}>
            <div className='price-item-label'>{t[key]()}</div>
            <div>{locale != 'ja' ? `${priceWithCommas(localCurrency.rate * price[key])}` : `${addNumberDelimiterForPrice(price[key])}円`}</div>
          </div>
        );
      } else if (price[key]) {
        elements.push(
          <div className="price-item" key={`${format(day, 'yyyy-MM-dd')}-${key}-price`}>
            <div className='price-item-label'>{t[key]()}</div>
            <div>{`JPY ${addNumberDelimiterForPrice(price[key])}`}</div>
          </div>
        );
      }
    });
    return (
      <div className="price">
        {elements}
      </div>
    )
  }

  renderCells() {
    const { priceTable } = this.props;
    const { currentMonth, selectedDateList, payableDateList } = this.state;
    const startDateOfMonth: Date    = startOfMonth(currentMonth);
    const endDateOfMonth: Date      = endOfMonth(currentMonth);
    const startDateOfCalendar: Date = startOfWeek(startDateOfMonth);
    const endDateOfCalendar: Date   = endOfWeek(endDateOfMonth);

    const days: JSX.Element[] = [];
    let day: Date = startDateOfCalendar;
    while (day <= endDateOfCalendar) {
      const cssDisabled = isPayable(priceTable, day) && this.isActiveDate(day) ? '' : 'disabled';
      const cssOutOfMonth = isSameMonth(day, startDateOfMonth) ? '' : 'out-of-month';
      const cssSelected = selectedDateList.some(d => isSameDay(d, day)) ? 'selected' : '';
      const cssTrailing = payableDateList.some(d => isSameDay(d, day)) ? '' : 'trailing';
      const cssWeekend = isWeekend(day) ? 'weekend' : '';
      
      days.push(
        <div
          className={`price-calendar__date ${cssDisabled} ${cssOutOfMonth} ${cssSelected} ${cssTrailing} ${cssWeekend}`}
          key={`date-${day}`}
          onClick={this.handleDateClick}
          data-date={format(day, 'yyyy-MM-dd')}
        >
          <div className="date">
            {format(day, 'd')}
          </div>
          
          {this.renderPrice(day)}
        </div>
      );
      day = addDays(day, 1);
    }
    return (
      <div className="price-calendar__dates">
        {days}
      </div>
    );
  }

  render() {
    return (
      <div className="price-calendar">
        <div>{this.renderHeader()}</div>
        <div>{this.renderDays()}</div>
        <div>{this.renderCells()}</div>
      </div>
    );
  }
}

 export default SnowItemCalendar;
